<template>
  <span
    v-if="isEmpty"
    class="_empty"
  >{{ $t('general.empty') }}</span>
  <span
    v-else-if="kind === 'users'"
    class="_users"
  >
    <user-prop
      v-if="users.length > 0"
      :users="users"
      show-user-name
      small
      wrap
    />
    <span
      v-for="index in deletedUserCount"
      :key="index"
      class="_empty"
    >{{ $t('goalChanges.deletedUser') }}</span>
  </span>
  <option-prop
    v-else-if="kind === 'options'"
    :options="options"
    wrap
  />
  <space-prop
    v-else-if="kind === 'spaces'"
    :spaces="spaces"
    wrap
  />
  <m-link
    v-else-if="kind === 'url' && href !== undefined"
    :href="href"
    target="_blank"
    inherit-color
    underlined
    class="_link"
  >
    {{ shortenText(text, URL_MAX_LENGTH) }}
  </m-link>
  <span
    v-else
    class="_text"
  >{{ text }}</span>
</template>

<script setup>
import OptionProp from '@/components/list/OptionProp.vue';
import SpaceProp from '@/components/list/SpaceProp.vue';
import UserProp from '@/components/list/UserProp.vue';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useUsers from '@/composables/user/users';
import { computed } from 'vue';
import { dateFormatter } from 'shared/lib/date';
import { linkHref } from '@/lib/url';
import { numberFormatter } from '@/lib/props/number';
import { propertyType } from 'shared/constants.json';
import { shortenText } from '@/lib/text';
import { textByLang } from 'shared/lib/language';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  property: { type: Object, required: true },
  propertyValue: { type: Object, default: null },
});

const URL_MAX_LENGTH = 40;

const { t } = useI18n();
const { userLang } = useLoggedInUser();
const userSvc = useUsers();

const kind = computed(() => {
  switch (props.property.type) {
    case propertyType.user:
      return 'users';
    case propertyType.options:
    case propertyType.singleSelect:
    case propertyType.status:
      return 'options';
    case propertyType.space:
      return 'spaces';
    case propertyType.url:
      return 'url';
    default:
      return 'text';
  }
});

const userIds = computed(() => (props.propertyValue?.users ?? []).map((u) => u.uid));
const users = computed(() => userSvc.selectMultiple(userIds.value).filter((u) => u !== undefined));
const deletedUserCount = computed(() => userIds.value.length - users.value.length);

const options = computed(() => (props.propertyValue?.selectedOptions ?? []).map((o) => {
  if (o.label === undefined) {
    return { ...o, text: t('goalChanges.deletedOption') };
  }
  return { ...o, text: textByLang(o.label, userLang.value) };
}));

const spaces = computed(() => props.propertyValue?.spaces ?? []);

const text = computed(() => {
  const pv = props.propertyValue;
  if (pv === null) {
    return '';
  }
  switch (props.property.type) {
    case propertyType.date:
      return dateFormatter(props.property.dateFormat, props.property.timeFormat, userLang.value, t)(pv.timestamp);
    case propertyType.number:
      return pv.number === null ? '' : numberFormatter(props.property.numberFormat, userLang.value)(pv.number);
    default:
      return pv.text ?? '';
  }
});

const href = computed(() => linkHref(text.value));

const isEmpty = computed(() => {
  switch (kind.value) {
    case 'users':
      return userIds.value.length === 0;
    case 'options':
      return options.value.length === 0;
    case 'spaces':
      return spaces.value.length === 0;
    default:
      return text.value === '';
  }
});
</script>

<style scoped lang="scss" type="text/scss">
  ._text {
    white-space: pre-wrap;
  }

  ._empty {
    color: $font-color-secondary;
  }

  ._users {
    display: inline-flex;
    flex-wrap: wrap;
    gap: .6rem;
    align-items: center;
  }

  ._link {
    overflow-wrap: anywhere;
  }
</style>
